import * as React from "react";
import { useState, useMemo, useCallback, useEffect } from "react";
import styled from "styled-components";
import { isPermitted } from "util/PermissionsMixin";
import { Button, Alert, Modal } from "components/bootstrap";
import { SourceCodeEditor } from "components/common";
import useCurrentUser from "hooks/useCurrentUser";
import useScopePermissions from "hooks/useScopePermissions";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import { useCreateSigmaRule, useValidateSigmaRule, useUpdateSigmaRule } from "security-app/hooks/useSigmaAPI";
import { Row, Col } from "security-app/components/common/FormBaseStyles";
import RuleOptionsForm from "security-app/components/SigmaRules/RuleOptionsForm";
import { formOptionsToPayload, INITIAL_RULE_OPTIONS } from "security-app/components/SigmaRules/RuleOptionsForm/utils";
const Buttons = ({
  onCancel,
  onConfirm,
  edit = false,
  confirmDisabled = false
}) => /* @__PURE__ */ React.createElement(Row, { $width: "100%", $gap: "0.5rem", $justify: "flex-end", $align: "center" }, /* @__PURE__ */ React.createElement(Button, { type: "reset", form: "ruleOptions", onClick: onCancel }, "Cancel"), /* @__PURE__ */ React.createElement(Button, { bsStyle: "primary", disabled: confirmDisabled, onClick: onConfirm }, `${edit ? "Save changes" : "Add rule"}`));
const StyledAlert = styled(Alert)`
  margin-top: 15px;
`;
function SigmaModal({ rule = INITIAL_RULE_OPTIONS, onConfirm, onCancel }) {
  const [currentRule, setCurrentRule] = useState(rule);
  const [ruleSchemaTouched, setRuleSchemaTouched] = useState(false);
  const [debouncedSourceValidation, setDebouncedSourceValidation] = useState(null);
  const [isTemporal, setIsTemporal] = useState(false);
  const [validationErrorsArr, setValidationErrorsArr] = useState([]);
  const [canSubmit, setCanSubmit] = useState(false);
  const sendTelemetry = useSendTelemetry();
  const { scopePermissions } = useScopePermissions(rule);
  const isMutable = useMemo(() => scopePermissions == null ? void 0 : scopePermissions.is_mutable, [scopePermissions]);
  const currentUser = useCurrentUser();
  const canManageRule = isPermitted(currentUser.permissions, `sigma_rules:edit:${currentRule.id}`) || !rule.id;
  const { createSigmaRule } = useCreateSigmaRule();
  const { updateSigmaRule } = useUpdateSigmaRule();
  const { validateSigmaRule } = useValidateSigmaRule();
  const validateRule = useCallback(
    async (rawRule) => {
      if (rawRule && canManageRule) {
        const validationResponse = await validateSigmaRule(rawRule);
        setValidationErrorsArr(validationResponse == null ? void 0 : validationResponse.errors);
        setIsTemporal(validationResponse == null ? void 0 : validationResponse.is_temporal);
      }
    },
    [canManageRule, validateSigmaRule]
  );
  useEffect(() => {
    if (rule.id) validateRule(rule.source);
  }, []);
  const debouncedRuleValidation = useCallback(
    (rawRule) => {
      if (debouncedSourceValidation) clearTimeout(debouncedSourceValidation);
      setDebouncedSourceValidation(
        setTimeout(() => {
          validateRule(rawRule);
        }, 800)
      );
    },
    [debouncedSourceValidation, validateRule]
  );
  const handleRuleInput = useCallback(
    (ruleYAML) => {
      setRuleSchemaTouched(true);
      setCurrentRule({ ...currentRule, source: ruleYAML });
      debouncedRuleValidation(ruleYAML);
    },
    [debouncedRuleValidation, currentRule]
  );
  const onOptionsChange = useCallback(
    (values) => {
      setCurrentRule({ ...currentRule, ...values, source: currentRule.source });
    },
    [currentRule]
  );
  const handleFormValidState = (formState) => {
    const newState = rule.id ? formState.isValid && (formState.dirty || ruleSchemaTouched) : formState.isValid;
    setCanSubmit(newState && !!currentRule.source && validationErrorsArr.length < 1 && canManageRule);
  };
  const handleConfirm = async () => {
    const optionsPayload = formOptionsToPayload(currentRule);
    if (rule.id) {
      await updateSigmaRule({ ...optionsPayload, id: rule.id, source: currentRule.source });
    } else {
      await createSigmaRule({
        ...optionsPayload,
        source: currentRule.source,
        share_request: currentRule.share_request
      });
    }
    sendTelemetry(TELEMETRY_EVENT_TYPE.SECURITY_APP[`SIGMA_RULE_${rule.id ? "UPDATED" : "CREATED"}`], {
      app_pathname: "security",
      app_section: "sigma"
    });
    onConfirm();
  };
  return /* @__PURE__ */ React.createElement(Modal, { show: true, onHide: onCancel }, /* @__PURE__ */ React.createElement(Modal.Header, null, /* @__PURE__ */ React.createElement(Modal.Title, null, `${rule.id ? "Edit" : "Add"} sigma rule`)), /* @__PURE__ */ React.createElement(Modal.Body, null, /* @__PURE__ */ React.createElement(Col, { $width: "100%" }, /* @__PURE__ */ React.createElement(Row, { $width: "100%", $fullWidth: true }, /* @__PURE__ */ React.createElement(
    SourceCodeEditor,
    {
      id: "sigmaEditor",
      mode: "yaml",
      theme: "light",
      height: 400,
      value: currentRule.source,
      onChange: handleRuleInput,
      onBlur: () => validateRule(currentRule.source),
      readOnly: !canManageRule || !isMutable
    }
  )), /* @__PURE__ */ React.createElement(Row, { $width: "100%", $fullWidth: true }, validationErrorsArr.length > 0 && /* @__PURE__ */ React.createElement(StyledAlert, { bsStyle: "danger" }, validationErrorsArr.map((errorStr) => /* @__PURE__ */ React.createElement("p", { key: errorStr }, errorStr)))), /* @__PURE__ */ React.createElement(
    RuleOptionsForm,
    {
      onSubmit: onOptionsChange,
      initialValues: currentRule,
      autoSubmit: true,
      validateOnMount: !!rule.id,
      isEditing: !!rule.id,
      formState: handleFormValidState,
      notMutable: !isMutable,
      readOnly: !canManageRule,
      isTemporal
    }
  ))), /* @__PURE__ */ React.createElement(Modal.Footer, null, /* @__PURE__ */ React.createElement(Buttons, { onCancel, edit: !!rule.id, onConfirm: handleConfirm, confirmDisabled: !canSubmit })));
}
export default SigmaModal;
